/**
 * Environment configuration for the frontend application
 * All environment variables should be accessed through this module
 * Never use import.meta.env directly in other files
 */

interface EnvConfig {
  // API Configuration
  apiUrl: string;

  // Clerk Authentication
  clerkPublishableKey: string;
  clerkDomain: string;

  // PostHog Analytics
  posthog: {
    apiKey: string;
    apiHost: string;
    enabled: boolean;
    autocapture: boolean;
    capturePageview: boolean;
    capturePageleave: boolean;
    sessionRecording: {
      enabled: boolean;
      maskAllInputs: boolean;
      maskAllText: boolean;
    };
  };

  // WebSocket Configuration
  websocketUrl: string;

  // Environment
  isDevelopment: boolean;
  isProduction: boolean;
  isStaging: boolean;
  environment: "development" | "staging" | "production";
}

// Validate required environment variables
function validateEnv(): void {
  const required = ["VITE_API_URL", "VITE_CLERK_PUBLISHABLE_KEY"];

  const missing = required.filter((key) => !import.meta.env[key]);

  if (missing.length > 0) {
    console.error(
      `Missing required environment variables: ${missing.join(", ")}`,
    );
  }
}

// Initialize and validate on import
validateEnv();

// Determine environment
const environment = import.meta.env.MODE as
  | "development"
  | "staging"
  | "production";
const isDevelopment = environment === "development";
const isProduction = environment === "production";
const isStaging = environment === "staging";

// Export singleton configuration
export const env: EnvConfig = {
  // API Configuration
  apiUrl: import.meta.env.VITE_API_URL || "http://localhost:8080",

  // Clerk Authentication
  clerkPublishableKey: import.meta.env.VITE_CLERK_PUBLISHABLE_KEY || "",
  clerkDomain: import.meta.env.VITE_CLERK_DOMAIN || "",

  // PostHog Analytics Configuration
  posthog: {
    apiKey: import.meta.env.VITE_POSTHOG_KEY || "",
    apiHost: import.meta.env.VITE_POSTHOG_HOST || "https://app.posthog.com",
    enabled: Boolean(import.meta.env.VITE_POSTHOG_KEY) && isProduction,
    autocapture: isProduction, // Only enable in production
    capturePageview: isProduction, // Only enable in production
    capturePageleave: isProduction, // Only enable in production
    sessionRecording: {
      enabled: isProduction, // Only enable session recording in production
      maskAllInputs: true, // Privacy: mask all input fields
      maskAllText: false, // Don't mask all text, only sensitive inputs
    },
  },

  // WebSocket Configuration
  websocketUrl:
    // Prefer explicit websocket URL (support legacy VITE_WS_URL and VITE_WEBSOCKET_URL)
    (import.meta.env.VITE_WS_URL as string | undefined) ||
    (import.meta.env.VITE_WEBSOCKET_URL as string | undefined) ||
    // Fallback to API URL when provided
    (import.meta.env.VITE_API_URL as string | undefined) ||
    // In production with no explicit vars, assume same-origin deployment
    ((isProduction && typeof window !== "undefined"
      ? window.location.origin
      : undefined) as string | undefined) ||
    // Local development default
    "http://localhost:8080",

  // Environment flags
  isDevelopment,
  isProduction,
  isStaging,
  environment,
};

// Export helper to check if analytics should be enabled
export const isAnalyticsEnabled = (): boolean => {
  return env.posthog.enabled && Boolean(env.posthog.apiKey);
};
